import {Checkbox, Flex, Text} from '@mantine/core';
import React, {useCallback, useRef, useState} from 'react';

import {getRegistrySupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {tagSchemaHasNoContainableType} from 'constants/SampleConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import StarIcon from 'nav/sidepanel/StarIcon';
import {TagSchemaSelectorQuery} from 'schemas/shared/__generated/TagSchemaSelectorQuery';
import {TAG_SCHEMA_SELECTOR_QUERY} from 'schemas/shared/TagSchemaOrInterfaceSelector';
import {MultiValueFilter} from 'search/SearchConstants';
import filterTagSchemas from 'tag-schema/filterTagSchemas';
import {toggleTagSchemaStar} from 'tag-schema/TagSchemaActions';
import {OptionData} from 'ui-library/menus/MenuConstants';

import DropdownFilterForm, {prefixedToggleText} from './base/DropdownFilterForm';
import {BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';

// Suffix shown on schemas that cannot hold inventory items.
const NOT_CONTAINABLE_SUFFIX = ' (Not containable)';

interface Props extends BaseFilterFormProps {
  filter: MultiValueFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: MultiValueFilter) => void;
  onRemove?: () => void;
}

/**
 * Secondary inventory search filter that lets users select one or more content (entity) schemas via
 * checkboxes. Storables that contain an item of any of the selected schemas are matched.
 *
 * Schemas without a containable type are still shown but rendered disabled, since they can never
 * hold inventory items.
 */
export default function ContainerContentSchemaFilterForm(props: Props): JSX.Element {
  // Query the complete tag schemas, translate to the REST shape (which carries `isContainable`/
  // `batchSchemaId`), and filter to the registry's entity/content schema set.
  const queryResponse = useSkippableBenchlingQuery<TagSchemaSelectorQuery>(TAG_SCHEMA_SELECTOR_QUERY);
  const isLoading = queryResponse.type === QueryResultType.PENDING;
  const tagSchemas =
    queryResponse.type === QueryResultType.SUCCESS
      ? filterTagSchemas(graphQLArrayToREST('TagSchema', queryResponse.data.tagSchemas), {
          oligoAwareFolderItemTypes: getRegistrySupportedOligoAwareTypes(),
        })
      : [];

  const starredSchemaIdsFromQuery =
    queryResponse.type === QueryResultType.SUCCESS
      ? queryResponse.data.starredTagSchemas.map((s) => s.id)
      : [];

  const starredSchemaIdsRef = useRef(starredSchemaIdsFromQuery);
  starredSchemaIdsRef.current = starredSchemaIdsFromQuery;

  // Optimistic local state so the star toggles immediately without waiting for the query to refetch.
  const [optimisticStarredIds, setOptimisticStarredIds] = useState<Set<string> | null>(null);
  const starredIds = optimisticStarredIds ?? new Set(starredSchemaIdsFromQuery);

  const handleToggleStar = useCallback((schemaId: string) => {
    setOptimisticStarredIds((prev) => {
      const current = prev ?? new Set(starredSchemaIdsRef.current);
      const next = new Set(current);
      if (next.has(schemaId)) {
        next.delete(schemaId);
      } else {
        next.add(schemaId);
      }
      return next;
    });
    toggleTagSchemaStar(schemaId);
  }, []);

  const selectedSchemaIds = props.filter.value ?? [];

  const allOptions: Array<OptionData> = tagSchemas
    .map((schema) => {
      const isNotContainable = tagSchemaHasNoContainableType(schema);
      return {
        name: isNotContainable ? `${schema.name}${NOT_CONTAINABLE_SUFFIX}` : schema.name,
        value: schema.id,
        disabled: isNotContainable,
        disabledMessage: isNotContainable ? 'Not containable' : undefined,
        className: 'schema-option',
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const starred = allOptions.filter((o) => starredIds.has(o.value as string));
  const unstarred = allOptions.filter((o) => !starredIds.has(o.value as string));

  const optionDatas: Array<OptionData> = [];
  if (starred.length > 0) {
    optionDatas.push({name: 'Starred', isHeader: true});
    optionDatas.push(...starred);
    optionDatas.push({name: 'Schemas', isHeader: true});
  }
  optionDatas.push(...unstarred);

  return (
    <DropdownFilterForm
      {...props}
      dropdownToggleText={prefixedToggleText('Content type', allOptions, props.filter)}
      filterStyle={props.filterStyle}
      isLoading={isLoading}
      isMulti={true}
      isSearchable={true}
      onChangeValue={(value: Array<string | null> | string | null | object) => {
        props.onChangeFilter({...props.filter, value: value as Array<string>});
      }}
      onRemove={props.onRemove}
      optionDatas={optionDatas}
      renderOptionContent={(optionData: OptionData) => (
        <Flex gap="md" align="center" wrap="nowrap" px="md">
          <Checkbox
            checked={!optionData.disabled && selectedSchemaIds.includes(optionData.value as string)}
            disabled={optionData.disabled}
            readOnly={true}
            size="md"
            tabIndex={-1}
            aria-hidden={true}
            style={{pointerEvents: 'none', flexShrink: 0}}
          />
          <Text c={optionData.disabled ? 'dimmed' : undefined} sx={{wordBreak: 'break-word', flex: 1}}>
            {optionData.name}
          </Text>
          {!optionData.disabled && (
            <StarIcon
              onClick={() => {
                handleToggleStar(optionData.value as string);
              }}
              starred={starredIds.has(optionData.value as string)}
            />
          )}
        </Flex>
      )}
      value={props.filter.value}
    />
  );
}
